import { Navigate, Outlet, useRoutes } from 'react-router-dom';

import * as Configs from '@app/configs';
import DashboardLayout from '@app/layouts/dashboard';
import { AuthGuard, GuestGuard } from '@app/guard';
import { Page404 } from '@app/components';
import { LoginPage, SetPasswordPage, ForgotPasswordPage } from '@app/modules/auth';
import { MyProfilePage } from '@app/modules/my-profile';
import { EmployeeDetailsPage, EmployeeListPage } from '@app/modules/employees';
import { DepartmentListPage } from '@app/modules/departments';
import { DesignationListPage } from '@app/modules/designation';
import { RoleListPage } from '@app/modules/role';
import { HolidayListPage } from '@app/modules/holidays';
import { PATH_AUTH, PATH_DASHBOARD } from './paths';
import { LeaveManagementPage } from '@app/modules/leave-setting';
import { ProjectsPage, ProjectDetailsPage } from '@app/modules/projects';
import { AddLeaveEmployee, LeaveDetailsPage } from '@app/modules/add-leave-employee';
import { AssetsListPage, AssetsDetailsPage } from '@app/modules/assets';
import { CompensationPage, OfferLetterPage } from '@app/modules/documents';
import { ClientDetailsPage, ClientListPage } from '@app/modules/clients';
import { CandidateListPage, CandidateDetailsPage } from '@app/modules/candidates';
import { IntervieweeListPage } from '@app/modules/interviewee';
import { InternDetailsPage, InternListPage } from '@app/modules/intern';
import { TestimonialPage } from '@app/modules/client-testimonial';
import { PortfolioPage } from '@app/modules/portfolio';
import { DashboardPage } from '@app/modules/dashboard';
import { ReviewDetailsPage, ReviewListPage } from '@app/modules/reviews';
import { EmployeeDashboardPage } from '@app/modules/employee-dashboard';
import { useAuthRole } from '@app/utils';
import { TaskBoardCard } from '@app/modules/task-board/view';
import { ManagerDashboardPage } from '@app/modules/manager-dashboard';
import { TasksDetailsPage, TasksListPage } from '@app/modules/tasks';
import { InternDashboardPage } from '@app/modules/intern-dashboard';
import { OverdueListPage } from '@app/modules/overdueTasks';
import { EtaListPage } from '@app/modules/eta';

// ----------------------------------------------------------------------

export function Router() {
  const { isSuperAdmin, isManager, isIntern } = useAuthRole();
  return useRoutes([
    {
      path: '/',
      element: (
        <AuthGuard>
          <Navigate to={Configs.PATH_AFTER_LOGIN} />
        </AuthGuard>
      ),
    },
    {
      element: (
        <GuestGuard>
          <Outlet />
        </GuestGuard>
      ),
      children: [
        {
          path: PATH_AUTH.login,
          element: <LoginPage />,
        },
        {
          path: PATH_AUTH.forgotPassword,
          element: <ForgotPasswordPage />,
        },
        {
          path: PATH_AUTH.setPassword,
          element: <SetPasswordPage />,
        },
        {
          path: '*',
          element: <Page404 />,
        },
      ],
    },
    {
      path: PATH_DASHBOARD.root,
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          element: <Navigate to={Configs.PATH_AFTER_LOGIN} replace />,
          index: true,
        },
        { path: PATH_DASHBOARD.myProfile, element: <MyProfilePage /> },
        {
          path: PATH_DASHBOARD.dashboard,
          element: isSuperAdmin ? (
            <DashboardPage />
          ) : isManager ? (
            <ManagerDashboardPage />
          ) : isIntern ? (
            <InternDashboardPage />
          ) : (
            <EmployeeDashboardPage />
          ),
        },
        {
          path: PATH_DASHBOARD.employeeList,
          element: <EmployeeListPage />,
        },
        {
          path: PATH_DASHBOARD.candidateList,
          element: <CandidateListPage />,
        },
        {
          path: PATH_DASHBOARD.internList,
          element: <InternListPage />,
        },
        {
          path: PATH_DASHBOARD.offerLetter,
          element: <OfferLetterPage />,
        },
        {
          path: PATH_DASHBOARD.compensationLetter,
          element: <CompensationPage />,
        },
        {
          path: PATH_DASHBOARD.leaveEmployee,
          element: <AddLeaveEmployee />,
        },
        {
          path: PATH_DASHBOARD.leaveDetail,
          element: <LeaveDetailsPage />,
        },
        {
          path: PATH_DASHBOARD.leaveManagement,
          element: <LeaveManagementPage />,
        },
        {
          path: PATH_DASHBOARD.departmentList,
          element: <DepartmentListPage />,
        },
        {
          path: PATH_DASHBOARD.designationList,
          element: <DesignationListPage />,
        },
        {
          path: PATH_DASHBOARD.roleList,
          element: <RoleListPage />,
        },
        {
          path: '*',
          element: <Page404 />,
        },
        {
          path: PATH_DASHBOARD.holidaysList,
          element: <HolidayListPage />,
        },
        {
          path: PATH_DASHBOARD.subProjects,
          element: <ProjectsPage />,
        },
        {
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          path: `${PATH_DASHBOARD.projectDetail}/:name`,
          element: <ProjectDetailsPage />,
        },
        {
          path: PATH_DASHBOARD.clients,
          element: <ClientListPage />,
        },
        {
          path: PATH_DASHBOARD.reviews,
          element: <ReviewListPage />,
        },
        {
          path: `${PATH_DASHBOARD.clientDetail}/:firstName`,
          element: <ClientDetailsPage />,
        },
        {
          path: PATH_DASHBOARD.assets,
          element: <AssetsListPage />,
        },
        {
          path: PATH_DASHBOARD.assetsDetail,
          element: <AssetsDetailsPage />,
        },
        {
          path: `${PATH_DASHBOARD.employeeDetail}/:firstName`,
          element: <EmployeeDetailsPage />,
        },
        {
          path: `${PATH_DASHBOARD.candidateDetail}/:firstName`,
          element: <CandidateDetailsPage />,
        },
        {
          path: PATH_DASHBOARD.IntervieweeList,
          element: <IntervieweeListPage />,
        },
        {
          path: `${PATH_DASHBOARD.internDetail}/:firstName`,
          element: <InternDetailsPage />,
        },
        {
          path: PATH_DASHBOARD.testimonial,
          element: <TestimonialPage />,
        },
        {
          path: PATH_DASHBOARD.portfolio,
          element: <PortfolioPage />,
        },
        { path: PATH_DASHBOARD.employeeDashboard, element: <EmployeeDashboardPage /> },
        {
          path: PATH_DASHBOARD.reviewDetail,
          element: <ReviewDetailsPage />,
        },
        {
          path: `${PATH_DASHBOARD.taskBoard}/:boardName?/:listId?/:taskId?`,
          element: <TaskBoardCard />,
        },
        {
          path: PATH_DASHBOARD.tasksList,
          element: <TasksListPage />,
        },
        {
          path: PATH_DASHBOARD.taskDetail,
          element: <TasksDetailsPage />,
        },
        {
          path: PATH_DASHBOARD.overdueTaskList,
          element: <OverdueListPage />,
        },
        {
          path: PATH_DASHBOARD.eta,
          element: <EtaListPage />,
        },
      ],
    },
  ]);
}
